import React, { useState, useEffect, useRef, useCallback } from 'react';
import cards from '../../../styles/shared/ProductCards.module.css';
import styles from './AdminProductCard.module.css';
import noImageIcon from '../../../images/no-image-icon.svg';
import { API_URL } from '../../../api/client';
import EditProductModal from '../EditProductModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit } from '@fortawesome/free-solid-svg-icons';

const AdminProductCard = React.memo(({ producto, isOpen, onOpen, onClose, deleteProducto }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const cardRef = useRef(null);
    const imageRef = useRef(null);

    useEffect(() => {
        if (producto.images && producto.images.length > 0) {
            setThumbnailUrl(`${API_URL}${producto.images[0].thumbnail}`);
            setImageUrl(`${API_URL}${producto.images[0].image}`);
        } else {
            setThumbnailUrl(null);
            setImageUrl(null);
        }
        setIsImageLoaded(false);
    }, [producto]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: '100px',
                threshold: 0.1
            }
        );

        const node = cardRef.current;
        if (node) {
            observer.observe(node);
        }

        return () => {
            if (node) {
                observer.unobserve(node);
            }
        };
    }, []);

    const handleImageLoad = useCallback(() => {
        setIsImageLoaded(true);
    }, []);

    useEffect(() => {
        const img = imageRef.current;

        if (isVisible && imageUrl && img) {
            if (img.complete) {
                handleImageLoad();
            } else {
                img.addEventListener('load', handleImageLoad);
            }

            return () => {
                if (img) {
                    img.removeEventListener('load', handleImageLoad);
                }
            };
        }
    }, [isVisible, imageUrl, handleImageLoad]);

    const handleDelete = useCallback(async (e) => {
        e.stopPropagation();
        const confirmDelete = window.confirm(`Seguro que quieres eliminar ${producto.equipo}?`);
        if (confirmDelete) {
            const success = await deleteProducto(producto.id);
            if (success) {
                window.location.reload();
            } else {
                console.error('Error deleting product');
            }
        }
    }, [producto.equipo, producto.id, deleteProducto]);

    const handleEdit = useCallback((e) => {
        e.stopPropagation();
        onOpen();
    }, [onOpen]);

    const isOutOfStock = producto.cantidad === 0;
    return (
        <div className={`${cards.cardWrapper} ${isOutOfStock ? cards.outOfStock : ''}`} ref={cardRef}>
            <div className={`card ${cards.card}`}>
                {isOutOfStock && <div className={cards.overlay}>Agotado</div>}
                <div className={styles.imageContainer}>
                    {thumbnailUrl && (
                        <img 
                            src={thumbnailUrl} 
                            alt={producto.equipo || 'Producto'} 
                            className={`card-img-top ${cards.cardImgTop} ${styles.thumbnail}`}
                        />
                    )}
                    {imageUrl && (
                        <img 
                            ref={imageRef}
                            src={imageUrl} 
                            alt={producto.equipo || 'Producto'} 
                            className={`card-img-top ${cards.cardImgTop} ${styles.fullImage} ${isImageLoaded ? styles.loaded : ''}`}
                        />
                    )}
                    {!thumbnailUrl && !imageUrl && (
                        <img src={noImageIcon} alt="No image available" className={`${styles.noImageIcon} card-img-top ${cards.cardImgTop}`} />
                    )}
                </div>
                <h6 className={cards.cardTitle2}>{producto.marca ? producto.marca : 'Marca no disponible'}</h6>
                <div className={`card-body ${cards.cardBody}`}>
                
                    <h5 className={`card-title ${cards.cardTitle}`}>{producto.equipo}</h5>
                       
                        <div className={cards.iconButtons}>
                            <FontAwesomeIcon 
                                icon={faEdit} 
                                className={styles.editIcon}
                                onClick={handleEdit} 
                            />
                             <p className={`card-text ${cards.cardText}`}><b>{producto.cantidad}</b></p>
                            <FontAwesomeIcon 
                                icon={faTrash} 
                                className={styles.trashIcon}
                                onClick={handleDelete} 
                            />
                    </div>
                </div>
            </div>
            {isOpen && (
                <EditProductModal
                    producto={producto}
                    onClose={onClose}
                />
            )}
        </div>
    );
});

export default AdminProductCard; 